import store from "../store/store";
import find from "../store/find";
import Contact from "../entities/contact";
import {getContactFromUser, getContactFromUserId} from "../lib/picos/getContactFromUser";
import {ReactElement} from "react";
import {md5hash} from "../lib/picos/hash";
import {hashIds} from '../lib/memoize';
import {getSystemUUID} from "../lib/picos/currentLogin";
import {uuid} from "../@types/schema";
import User from "../entities/user";
import ArrayMap from "../lib/ArrayMap";
import newHRTimestamp from "../lib/picos/timestamp";

// Cache for isUnique
type IsUniqueCacheEntry = { result: boolean; depsHash: string };
const isUniqueCache = new Map<string, IsUniqueCacheEntry>();

function computeIsUniqueDepsHash(limitTo: Contact[] | undefined): string {
    const state = store.getState();
    if (limitTo) {
        // When using a limited list, hash the limitTo IDs + users
        return hashIds([
            ...limitTo.map(c => c.id),
            ...state.users.map(u => u.id),
        ]);
    }
    // When using all contacts, hash contacts + users
    return hashIds([
        ...state.contacts.map(c => c.id),
        ...state.users.map(u => u.id),
    ]);
}

function computeIsUniqueCacheKey(name: ContactName, lengths: ContactNameLengths, limitTo: Contact[] | undefined): string {
    // Hash the name and lengths objects along with limitTo IDs
    const nameStr = JSON.stringify(name);
    const lengthsStr = JSON.stringify(lengths);
    const limitToStr = limitTo ? limitTo.map(c => c.id).sort().join(',') : 'all';
    return md5hash(`${nameStr}|${lengthsStr}|${limitToStr}`);
}

export enum FormatChoice {
    'LFM',
    'FML'
}

export type FormatChoiceType = keyof typeof FormatChoice;

export type ContactName = {
    first?: string;
    middle?: string;
    last?: string;
    suffix?: string;
}

export type ContactNameLengths = {
    first?: number;
    middle?: number;
    last?: number;
    suffix?: number;
}

export type UserNameFormatOptions = {
    shorten?: boolean; // Should the name be shortened to it's bare minimum?
    format?: FormatChoiceType; // defaults to FML
    useInitials?: boolean; // Use initials instead of full name?
}

/**
 * Determine if a given contact's name parts are unique globally
 *
 * @param {ContactName} name - The name of the contacts
 * @param {ContactNameLengths} lengths - how many characters of each name should be considered? Defaults to all
 * @param {Contact[]} limitTo - Limit to a specific set of contacts
 * @returns {boolean}
 */
export const isUnique = (name: ContactName, lengths: ContactNameLengths = {}, limitTo?: Contact[]): boolean => {
    const cacheKey = computeIsUniqueCacheKey(name, lengths, limitTo);
    const depsHash = computeIsUniqueDepsHash(limitTo);

    // Check cache
    const cached = isUniqueCache.get(cacheKey);
    if (cached && cached.depsHash === depsHash) {
        return cached.result;
    }

    // Compute
    const result = _computeIsUnique(name, lengths, limitTo);

    // Cache and return
    isUniqueCache.set(cacheKey, { result, depsHash });
    return result;
};

function _computeIsUnique(name: ContactName, lengths: ContactNameLengths = {}, limitTo?: Contact[]): boolean {
    let {first, middle, last, suffix} = name;
    if (!!first && !!lengths?.first) first = first.trim().slice(0, lengths.first);
    if (!!middle && !!lengths?.middle) middle = middle.trim().slice(0, lengths.middle);
    if (!!last && !!lengths?.last) last = last.trim().slice(0, lengths.last);
    if (!!suffix && !!lengths?.suffix) suffix = suffix.trim().slice(0, lengths.suffix);

    const contactList = limitTo ? limitTo : store.getState().contacts;

    // For each contact, determine if there is a match
    const matches = contactList
        .filter(c => {
            // Quick exit criteria
            if (!c || c.status === 'D') return false;

            // Only worry about defined parts.  Null is NOT undefined, and we will look for matches
            if (first !== undefined) {
                const cFirst = c.first ? lengths?.first ? c.first.trim().slice(0, lengths.first) : c.first.trim() : null;

                if (!cFirst) return false; // Already know first is not null, so if cFirst is null, done
                else if (lengths.first && cFirst.slice(0, lengths.first) !== first) return false; else if (cFirst !== first) return false;
            }

            if (last !== undefined) {
                const cLast = c.last ? lengths?.last ? c.last.trim().slice(0, lengths.last) : c.last.trim() : null;

                if (!cLast) return false; // Already know last is not null, so if cLast is null, done
                else if (lengths.last && cLast.slice(0, lengths.last) !== last) return false; else if (cLast !== last) return false;
            }

            if (middle !== undefined) {
                const cMiddle = c.middle ? lengths?.middle ? c.middle.trim().slice(0, lengths.middle) : c.middle.trim() : null;

                if (!cMiddle) return false; // Already know middle is not null, so if cMiddle is null, done
                else if (lengths.middle && cMiddle.slice(0, lengths.middle) !== middle) return false; else if (cMiddle !== middle) return false;
            }

            if (suffix !== undefined) {
                const cSuffix = c.suffix ? lengths?.suffix ? c.suffix.trim().slice(0, lengths.suffix) : c.suffix.trim() : null;

                if (!cSuffix) return false; // Already know middle is not null, so if cSuffix is null, done
                else if (lengths.suffix && cSuffix.slice(0, lengths.suffix) !== middle) return false; else if (cSuffix !== suffix) return false;
            }

            return true;
        });

    return matches.length <= 1;
}

/**
 * Mask a string.
 *
 * @param {string} text
 * @return {ReactElement}
 */
export function maskText(text: string): ReactElement {
    return <div className='u-masked-entry'>
        <span className="u-unmasked">{text.substring(0, 4)}</span>
        <span className='u-masked'>{text.substring(4, text.length - 1)}</span>
    </div>;
}

/**
 *
 * @param {string} text
 * @param {number} index
 * @returns {ReactElement[]}
 */
export function maskTextEntry(text: string, index: number): ReactElement[] {
    return [<span key={`unmasked-${text}-${index}`} className="u-unmasked">{text.substring(0, 4)}</span>,
        <span key={`masked-${text}-${index}`} className='u-masked'>{text.substring(4, text.length - 1)}</span>];
}

/**
 * Return the name of the contact formatted as First Middle Last
 *
 * @param {Contact} contact
 * @param {boolean} [includeMiddle] - should middle names be included?
 * @returns {string}
 */
export const formatFML = (contact: Contact | null | undefined, includeMiddle?: boolean): string => {
    if (!contact) {
        console.debug('Contact is null');
        return 'No Contact Found';
    }

    let name = '';
    if (contact.nick) name += `${contact.first} `;
    if (!contact.nick && contact.first) name += `${contact.first || ''} `;
    if (includeMiddle && contact.middle) name += `${contact.middle || ''} `;
    if (contact.last) name += `${contact.last || ''} `;
    if (contact.suffix) name += `${contact.suffix || ''}`;

    return name.trim();
};

/**
 * Return the name of the contact formatted as Last, First Middle
 *
 * @param {Contact} contact
 * @param {boolean} [includeMiddle] - should middle names be included?
 * @returns {string}
 */
export const formatLFM = (contact: Contact | null | undefined, includeMiddle?: boolean): string => {
    if (!contact) {
        console.debug('formatLFM called with null contact');
        return '';
    }

    if (contact.lfm) return contact.lfm;

    // Start with the last name, if available
    let name: string;

    name = contact.last ?? '';
    if (contact.nick || contact.first || contact.middle) name += ', ';

    const rest = [];
    if (contact.nick) rest.push(contact.nick);
    else if (contact.first) rest.push(contact.first);
    // if (contact.nick) rest.push(`"${contact.nick}"`);
    if (includeMiddle && contact.middle) rest.push(contact.middle);
    if (contact.suffix) rest.push(contact.suffix);

    const result = name + rest.join(' ');

    // Update the contact
    contact.lfm = result;

    return result;
};

export const formatFMLFromId = (id: uuid, includeMiddle?: boolean) => formatFML(find<Contact>('contacts', id) as Contact, includeMiddle);

export const formatLFMFromId = (id: uuid, includeMiddle?: boolean) => formatLFM(find<Contact>('contacts', id) as Contact, includeMiddle);

/**
 * Format a name as short as possible while still being unique.
 *
 * PERF: Tested at .01 to .04ms
 *
 * @param {Contact} contact or contactId
 * @param {FormatChoiceType} preferredFormat - either LFM or FML (determines if we start with last name or first name)
 * @param {Contact[]} [limitTo] - Limit to a specific set of contacts
 * @returns {string}
 */
export const formatUniqueName = (contact: Contact, preferredFormat: string = 'LFM', limitTo?: Contact[] | null): string => {
    if (!contact) return 'Unknown';

    if (contact.isCompany) return contact.last || '';

    // Only use cached values when no specific limitTo set is provided
    if (!limitTo) {
        if (preferredFormat === 'FML' && !!contact.uniqueFML && (contact.uniqueFMLComputed >= contact.addedOn)) return contact.uniqueFML;
        else if (preferredFormat === 'LFM' && !!contact.uniqueLFM && (contact.uniqueLFMComputed >= contact.addedOn)) return contact.uniqueLFM;
    }

    // Jr., Sr., etc.
    const last = contact.last;
    const first = contact.nick ?? contact.first;
    const middle = contact.middle;
    const suffix = contact.suffix;

    let result: string;

    if (preferredFormat === 'LFM') {
        if (last && isUnique({last}, {}, limitTo)) result = last;
        else if (last && first && isUnique({
            last,
            first
        }, {first: 1}, limitTo)) result = `${last}, ${first[0]}.`;
        else if (last && first && isUnique({last, first}, {}, limitTo)) result = `${last}, ${first}`;
        else if (last && first && middle && isUnique({
            last,
            first,
            middle
        }, {middle: 1}, limitTo)) result = `${last}, ${first} ${middle[0]}.`;
        else if (last && first && middle && isUnique({
            last,
            first,
            middle,
            suffix
        }, {middle: 1}, limitTo)) result = `${last}, ${first} ${middle[0]}. ${suffix}`;
        else result = `${last ? last : ''}, ${first ? first : ''} ${middle ? middle : ''} ${suffix ? suffix : ''}`.trim();
    }
    else {
        if (first && isUnique({first}, {}, limitTo)) result = first;
        else if (first && last && isUnique({first, last}, {last: 1}, limitTo)) result = `${first} ${last[0]}.`;
        else if (first && last && isUnique({first, last}, {}, limitTo)) result = `${first} ${last}`;
        else if (first && middle && last && isUnique({
            first,
            middle,
            last
        }, {}, limitTo)) result = `${first} ${middle[0]}. ${last}`;
        else if (first && middle && last && suffix && isUnique({
            first,
            middle,
            last,
            suffix
        }, {}, limitTo)) result = `${first} ${middle[0]}. ${last}, ${suffix}`;
        else result = `${first ? first : ''} ${middle ? middle : ''} ${last ? last : ''} ${suffix ? suffix : ''}`.trim();
    }

    // Only cache when computed against the default set (no specific limitTo)
    if (!limitTo) {
        if (preferredFormat === 'LFM') {
            contact.uniqueLFM = result;
            contact.uniqueLFMComputed = newHRTimestamp();
        }
        else if (preferredFormat === 'FML') {
            contact.uniqueFML = result;
            contact.uniqueFMLComputed = newHRTimestamp();
        }
    }

    return result;
};

export const formatUniqueNameFromId = (id: uuid, preferredFormat: string, limitTo?: Contact[]) => formatUniqueName(find<Contact>('contacts', id) as Contact, preferredFormat, limitTo);

/**
 *
 * @param {Contact[]} contacts - a list of contacts to reference
 * @param {uuid} contactId - the contact id to format
 * @param {FormatChoiceType} preferredFormat - Last First Middle vs First Middle Last
 * @param {Contact[]} limitTo - Limit to a specific set of contacts
 * @param {Contact} [additionalContact]
 * @returns {string}
 */
export const formatUniqueNameOverride = (contacts: Contact[] | ArrayMap<Contact>, contactId: uuid, preferredFormat?: FormatChoiceType, limitTo?: Contact[], additionalContact?: Contact): string => {
    // Nulls are unknown
    if (!contactId) return 'Unknown';

    if (!preferredFormat) preferredFormat = 'LFM';

    let contact = additionalContact?.id === contactId
                  ? additionalContact
                  : contacts.find(c => c.id === contactId && c.status !== 'D');

    // Nothing? Search deleted contacts
    if (!contact) contacts.find(c => c.id === contactId);

    // Still nothing, bail out.
    if (!contact) {
        console.debug(`contactId ${contactId} not found in store.getState().contacts?`);
        return 'Unknown';
    }

    if (contact.isCompany) return contact.last || '';

    if (preferredFormat === 'FML' && contact.uniqueFML && contact.uniqueFMLComputed >= contact.addedOn) return contact.uniqueFML;
    else if (preferredFormat === 'LFM' && contact.uniqueLFM && contact.uniqueLFMComputed >= contact.addedOn) return contact.uniqueLFM;


    // Jr., Sr., etc.
    const last = contact.last;
    const first = contact.nick ?? contact.first;
    const middle = contact.middle;
    const suffix = contact.suffix;

    let result: string;

    if (preferredFormat === 'LFM') {
        if (last && isUnique({last}, {}, limitTo)) result = last;
        else if (last && first && isUnique({
            last,
            first
        }, {first: 1}, limitTo)) result = `${last}, ${first[0]}.`;
        else if (last && first && isUnique({last, first}, {}, limitTo)) result = `${last}, ${first}`;
        else if (last && first && middle && isUnique({
            last,
            first,
            middle
        }, {middle: 1}, limitTo)) result = `${last}, ${first} ${middle[0]}.`;
        else if (last && first && middle && isUnique({
            last,
            first,
            middle,
            suffix
        }, {middle: 1}, limitTo)) result = `${last}, ${first} ${middle[0]}. ${suffix}`;
        else result = `${last ? last : ''}, ${first ? first : ''} ${middle ? middle : ''} ${suffix ? suffix : ''}`.trim();
    }
    else {
        if (first && isUnique({first}, {}, limitTo)) result = first;
        else if (first && last && isUnique({first, last}, {last: 1}, limitTo)) result = `${first} ${last[0]}.`;
        else if (first && last && isUnique({first, last}, {}, limitTo)) result = `${first} ${last}`;
        else if (first && middle && last && isUnique({
            first,
            middle,
            last
        }, {}, limitTo)) result = `${first} ${middle[0]}. ${last}`;
        else if (first && middle && last && suffix && isUnique({
            first,
            middle,
            last,
            suffix
        }, {}, limitTo)) result = `${first} ${middle[0]}. ${last}, ${suffix}`;
        else result = `${first ? first : ''} ${middle ? middle : ''} ${last ? last : ''} ${suffix ? suffix : ''}`.trim();
    }

    if (preferredFormat === 'LFM') {
        contact.uniqueLFM = result;
        contact.uniqueLFMComputed = newHRTimestamp();
    }
    else if (preferredFormat === 'FML') {
        contact.uniqueFML = result;
        contact.uniqueFMLComputed = newHRTimestamp();
    }

    return result;
}

export const sortContacts = (a: Contact, b: Contact): number => {
    return formatLFM(a, true).localeCompare(formatLFM(b, true));
};

/**
 *
 * @param {uuid} userId
 * @param {1|2|3} [numLetters]
 * @returns {string}
 */
export const getUserInitials = (userId: uuid, numLetters: 1 | 2 | 3 = 3): string => {
    const contact = getContactFromUserId(userId);
    if (contact) {
        const first = contact.first ? contact.first[0] : '';
        const middle = contact.middle ? contact.middle[0] : '';
        const last = contact.last ? contact.last[0] : '';

        if (numLetters === 1) return first?.toUpperCase() || last.toUpperCase();
        else if (numLetters === 2) return (first + last).toUpperCase();
        else return (first + middle + last).toUpperCase();
    }
    else return '?';
}

/**
 * Replace names with the text given for contact names. Used in demo mode.
 *
 * @param {uuid[] | null} contactIds - uuids of the contacts to scan for
 * @param {string} text - the text to scan
 */
export const replaceNames = (contactIds: uuid[], text: string) => {
    if (!text || text.length < 3) return text;

    const contacts = (contactIds?.length > 0)
                     ? store.getState().contacts.getIndex('notDeleted').filter(c => contactIds.includes(c.id))
                     : store.getState().contacts.getIndex('notDeleted');

    // Get a unique list of names
    const names = new Set();
    contacts.forEach(c => {
        if (c.first) names.add(c.first);
        if (c.last) names.add(c.last);
    });

    // Sort by length first (largest wins)
    const sortedNames: string[] = (Array.from(names.keys()) as string[])
        .sort((a: string, b: string): number => {
            if (!a) return 1;
            else if (!b) return -1;
            else return b.length - a.length
        });

    // Create array of terms from text
    const toReplace = text.split(' ');

    // For each term in toReplace, go through each name and see if it matches (from position 0 only!)
    const afterReplace = toReplace.map(term => {
        let replaced: any = null;

        sortedNames.forEach((name, idx) => {
            if (!!replaced) return;

            const index = term.indexOf(name);
            if (index === 0) {
                // Replace with div
                replaced = maskTextEntry(term, idx);
            }
        });

        return replaced || term;
    });

    return <div key={md5hash(text)} className='u-masked-entry'>
        {afterReplace.map((entry, index) => typeof (entry) === 'string' ? entry + ' ' :
                                            <div key={`${index}e`} className='u-add-space'>{entry}</div>)}
    </div>;
};

/**
 * Return the name associated with this user.
 * - If not related to a contact, return the email address
 * - If options.shorten, return the smallest name that uniquely identifies a person
 *
 * @param {User} user
 * @param {UserNameFormatOptions} [options]
 * @param {Contact[]} [contactOverrides] - Contacts to use for comparison
 */
export const formatUserName = (user: User, options?: UserNameFormatOptions, contactOverrides?: Contact[]): string => {
    if (!user) return '';

    try {
        // Use precomputed values if possible
        const useInitials = options?.useInitials;
        if (useInitials && user.initials) return user.initials || '';

        const shorten = options?.shorten;
        if (shorten && user.shorten && !contactOverrides) return user.shorten || '';

        const format = options?.format || 'FML';

        if (!shorten && !useInitials) {
            if (format === 'FML' && user.fml) return user.fml || '';
            if (format === 'LFM' && user.lfm) return user.lfm || '';
        }

        // Compute the user's name
        if (user.contactId) {
            const contact = getContactFromUser(user);
            if (!contact) return '';

            if (useInitials) {
                const f = contact.first ? contact.first[0] : '';
                const m = contact.middle ? contact.middle[0] : '';
                const l = contact.last ? contact.last[0] : '';
                return `${f}${m}${l}`.trim().toUpperCase() || '';
            }

            // If there is no first or last name, return email
            if (!contact.first && !contact.last) return user.email || '';

            const contactList: Contact[] = contactOverrides ?? (shorten ? store.getState().users
                                                       .filter(u => !!u.contactId)
                                                       .map(u => find<Contact>('contacts', u.contactId as uuid))
                                                       .filter(u => !!u)
                                                   : []);

            if (shorten) return formatUniqueName(contact, format, contactList);
            else if (format === 'FML') return formatFML(contact, false);
            else return formatLFM(contact, false);
        }
        else return '';
    }
    catch (err) {
        console.error('Error in formatUserName', err);
        return '';
    }
};

/**
 *
 * @param {uuid} userId (or contactId)
 * @param {UserNameFormatOptions} [options]
 */
export const formatUserNameFromId = (userId: uuid, options: UserNameFormatOptions = {}): string => {
    if (userId === getSystemUUID()) {
        if (!!options?.format) {
            return 'SYS';
        }
        else return 'Outlaw Autobot';
    }

    // If we haven't loaded the user table yet, defer
    if (store.getState().users.length === 0) return '';

    try {
        const user = find<User>('users', userId);

        // If no user found, this is likely a contactId
        if (!user) {
            const contactId = userId;
            if (options?.format) {
                if (options.format === 'LFM') return formatLFMFromId(contactId);
                else return formatFMLFromId(contactId);
            }
            else return formatFMLFromId(contactId);
        }
        else return formatUserName(user, options);
    }
    catch (err) {
        console.error(err);
        return '';
    }
};

/**
 * Mask everything except the last 3 letters before the @, and the first 3 after it.
 *
 * @param {string} email
 * @returns {string | ReactElement}
 */
export const maskEmail = (email: string): string | ReactElement => {
    if (!email) return email;

    function mask(str: string): ReactElement {
        return <>
            <span className="u-unmasked">{str.substring(0, 3)}</span>
            <span className='u-masked'>{str.substring(3, str.length - 1)}</span>
        </>;
    }

    // Split the email at the @
    let [beginning, end] = email.split('@');

    const first = mask(beginning);

    const domainIndex = end.lastIndexOf('.');
    const server = mask(end.substring(0, domainIndex));
    const domain = mask(end.substring(domainIndex + 1));

    return (<div key={email} className='u-masked-entry'>
        {first}@{server}.{domain}
    </div>);
};